import ChevronDownIcon from '@heroicons/react/20/solid/ChevronDownIcon.js';
import MagnifyingGlassIcon from '@heroicons/react/24/outline/MagnifyingGlassIcon.js';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon.js';
import React from 'react';
import { useObjectRef } from 'react-aria';
import {
  Button as AriaButton,
  ButtonContext as AriaButtonContext,
  Group,
  Input as AriaInput,
  type Key,
  SearchField as AriaSearchField,
  Select as AriaSelect,
  SelectStateContext,
  SelectValue as AriaSelectValue,
} from 'react-aria-components';

import { forwardRef } from '../../generic-forward-ref';
import { tv } from '../../tv';
import { Autocomplete } from '../autocomplete/autocomplete';
import { FieldAction, FieldActionContext, fieldSeatClass } from '../field/field-action';
import {
  adornmentStyles,
  entryStyles,
  FieldLabel,
  FieldMessage,
  type FrameState,
  frameState,
  frameStyles,
  resolveFieldState,
  useFieldLayout,
  useFieldMessageIds,
} from '../field/field-shell';
import { type PickerValue, samePickerValue, useOwnedState } from '../field/owned-state';
import { type ChipEntry, PickerChips } from '../list-box/chips';
import {
  type OptionItemsDynamicProps,
  type OptionItemsStaticProps,
  OptionList,
} from '../list-box/list-box';
import { readOptionMeta } from '../list-box/option';
import {
  ListSuppressionContext,
  useListSuppression,
  withoutSuppressedToggles,
} from '../list-box/suppression';
import { PickerPopup } from '../overlay/picker-popup';
import { Tag } from '../tag/tag';

const selectStyles = tv({
  slots: {
    /* The trigger is the frame's entry seat as a button: the entry recipe
     * gives it its text, its baseline and its stretch; this adds a button's
     * alignment. Its focus look lives on the frame (field-shell). */
    trigger: 'ck:flex ck:items-center ck:text-left ck:cursor-pointer ck:select-none',
    value: 'ck:flex ck:min-w-0 ck:flex-1 ck:items-center',
    text: 'ck:min-w-0 ck:truncate',
    placeholder: 'ck:min-w-0 ck:truncate ck:text-text-muted',
    chips: 'ck:flex ck:min-w-0 ck:flex-1 ck:flex-wrap ck:items-center ck:gap-1 ck:font-sans',
    /* The filter above the options: the md search frame inset by the list's
     * padding, under a hairline so it reads as the list's head, not a row. */
    filter: 'ck:border-b ck:border-border-subtle ck:p-1',
  },
  variants: {
    size: {
      sm: { trigger: 'ck:gap-1', value: 'ck:gap-1' },
      /* At least one chip row tall, so an empty field with no placeholder
       * keeps the field's height. */
      md: { trigger: 'ck:gap-2', value: 'ck:gap-2', chips: 'ck:text-sm ck:min-h-5' },
      lg: { trigger: 'ck:gap-2', value: 'ck:gap-2', chips: 'ck:text-base ck:min-h-6' },
    },
    singleLine: {
      true: { chips: 'ck:flex-nowrap ck:overflow-hidden' },
      false: {},
    },
    /* Nothing opens, so the hand goes. */
    readOnly: {
      true: { trigger: 'ck:cursor-default' },
      false: {},
    },
  },
});

interface SelectCommonProps {
  /**
   * Help under the field: the rule the value must meet, or why the field is
   * read-only. Inline content only (Text, Link, Code runs), never blocks.
   * Hidden while `error` shows, since the error restates the rule.
   */
  description?: React.ReactNode;
  /**
   * The error message. The field is invalid exactly when this is non-empty.
   * Say what to do: "Choose a framework", never "Invalid value".
   */
  error?: string;
  /**
   * Marks the label with an asterisk. The trigger is a button and carries
   * no `aria-required`, so here the mark is read by assistive technology.
   */
  isRequired?: boolean;
  /** Washed out and skipped by Tab, its clear with it. Prefer `isReadOnly` whenever the value still matters. */
  isDisabled?: boolean;
  /**
   * Full contrast and focusable, but not changeable: the list never opens
   * and no key moves the value. Say why in `description`.
   */
  isReadOnly?: boolean;
  /** Shown while nothing is chosen. Absent, the trigger is blank. */
  placeholder?: string;
  /** The form field name; the value is submitted through a hidden select. */
  name?: string;
  /** The id of the trigger element. */
  id?: string;
  /** Whether the trigger takes focus on mount. */
  autoFocus?: boolean;
  /** Called when the trigger loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  /** Called when the trigger receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  /** Whether the list is open (controlled). */
  isOpen?: boolean;
  /** Whether the list is open on mount (uncontrolled). */
  defaultOpen?: boolean;
  /** Called when the list opens or closes. */
  onOpenChange?: (isOpen: boolean) => void;
  /** Values the render function closes over, so a change re-renders the options. */
  dependencies?: unknown[];
}

interface SelectSingleValueProps {
  /** One choice (the default), or several shown as chips in the frame. */
  selectionMode?: 'single';
  /** The chosen option's id, or null for none; in the multiple form, the chosen ids in the order chosen (controlled). */
  value?: string | null;
  /** The value on mount (uncontrolled), restored by a form reset. */
  defaultValue?: string | null;
  /** Called with the new value: the chosen id or null, or in the multiple form the chosen ids. */
  onChange?: (value: string | null) => void;
  removeLabel?: never;
  isSingleLine?: never;
}

interface SelectLabelledProps {
  /** The visible name, above the field or beside it (Form decides). */
  label: string;
  'aria-label'?: never;
  'aria-labelledby'?: never;
}

interface SelectUnlabelledProps {
  label?: never;
  /** The accessible name of a picker with no visible label (a toolbar). */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface SelectLabelledByProps {
  label?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the field (a question's text). */
  'aria-labelledby': string;
}

interface SelectSmallProps {
  /** Height on the Button ramp: sm 24px, md 32px, lg 40px; the multiple form takes md and lg. */
  size: 'sm';
  /** A 24px frame seats no clear: the smallest action is its own height. */
  isClearable?: never;
  clearLabel?: never;
}

interface SelectUnclearableProps {
  /** Height on the Button ramp: sm 24px, md 32px, lg 40px; the multiple form takes md and lg. */
  size?: 'md' | 'lg';
  isClearable?: false;
  clearLabel?: never;
}

interface SelectClearableProps {
  /** Height on the Button ramp: sm 24px, md 32px, lg 40px; the multiple form takes md and lg. */
  size?: 'md' | 'lg';
  /** Seats a clear at the frame's end once a value is chosen; `onChange(null)`. */
  isClearable: true;
  /** Names the icon-only clear ("Clear framework"). Required with `isClearable`. */
  clearLabel: string;
}

interface SelectUnfilterableProps {
  isFilterable?: false;
  filterLabel?: never;
  filterPlaceholder?: never;
  filterEmptyMessage?: never;
}

interface SelectFilterableProps {
  /**
   * A search at the head of the list narrows the options as the user types:
   * for a closed list too long to scan (countries, frameworks). Escape
   * clears the text, then closes the list.
   */
  isFilterable: true;
  /** The accessible name of the filter entry ("Filter frameworks"). */
  filterLabel: string;
  /** Example text in the filter entry. */
  filterPlaceholder?: string;
  /**
   * Shown in place of the options when what is typed matches none ("No
   * matches"). A list with no options at all shows no filter and says why
   * instead (`emptyMessage`).
   */
  filterEmptyMessage: string;
}

/* Options from data can run out (nothing installed yet, a provider that
 * failed), and an empty list still opens, so it has to say why. */
type SelectOptionsProps<T> =
  | (OptionItemsStaticProps & {
      /** Shown in place of the options when there are none. */
      emptyMessage?: string;
    })
  | (OptionItemsDynamicProps<T> & {
      /**
       * Shown in place of the options when there are none: the list opens
       * and says why ("No connectors yet. Install one in Integrations.").
       */
      emptyMessage: string;
    });

interface SelectMultipleProps {
  /** Several choices, shown as chips in the frame that wrap and grow it; the list stays open while picking. */
  selectionMode: 'multiple';
  /** The chosen option's id, or null for none; in the multiple form, the chosen ids in the order chosen (controlled). */
  value?: string[];
  /** The value on mount (uncontrolled), restored by a form reset. */
  defaultValue?: string[];
  /** Called with the new value: the chosen id or null, or in the multiple form the chosen ids. */
  onChange?: (value: string[]) => void;
  /** Names each chip's remove button from its label ("Remove SOC 2"). Required with multiple. */
  removeLabel: (label: string) => string;
  /** Height on the Button ramp: sm 24px, md 32px, lg 40px; the multiple form takes md and lg. */
  size?: 'md' | 'lg';
  /** Each chip is its own clear; the single-line form adds one for all. */
  isClearable?: never;
}

interface SelectWrappingProps {
  isSingleLine?: false;
  clearLabel?: never;
}

interface SelectSingleLineProps {
  /**
   * Keeps the chips on one row: the ones that fit, then a "+N" count of the
   * rest, named by their labels. For a toolbar filter, whose height must
   * not grow; a form field wraps. The chips behind "+N" cannot be reached
   * one by one, so the frame seats a clear for all of them.
   */
  isSingleLine: true;
  /** Names the icon-only clear of every chip ("Clear approvers"). */
  clearLabel: string;
}

export type SelectProps<T = object> = SelectCommonProps &
  SelectOptionsProps<T> &
  (SelectLabelledProps | SelectUnlabelledProps | SelectLabelledByProps) &
  (
    | (SelectSingleValueProps & (SelectSmallProps | SelectUnclearableProps | SelectClearableProps))
    | (SelectMultipleProps & (SelectWrappingProps | SelectSingleLineProps))
  ) &
  (SelectUnfilterableProps | SelectFilterableProps);

type SelectState = NonNullable<React.ContextType<typeof SelectStateContext>>;

/* The option keys in reading order, sections flattened. */
function optionKeys(collection: SelectState['collection']): Key[] {
  const keys: Key[] = [];
  for (const node of collection) {
    if (node.type === 'item') keys.push(node.key);
    if (node.type !== 'section') continue;
    for (const child of collection.getChildren?.(node.key) ?? []) {
      if (child.type === 'item') keys.push(child.key);
    }
  }
  return keys;
}

/**
 * The chosen option as the closed picker shows it: its text, with the
 * glyph, the avatar or the Tag color the option declared. React Aria's own
 * value element, so the trigger's name stays "value, label".
 */
function SelectValueText({
  placeholder,
  size,
}: {
  placeholder?: string;
  size: 'sm' | 'md' | 'lg';
}) {
  const state = React.useContext(SelectStateContext);
  const { value, text, placeholder: placeholderClass } = selectStyles({ size });
  const { icon } = adornmentStyles({ size });
  return (
    <AriaSelectValue className={value()}>
      {({ isPlaceholder, selectedText }) => {
        if (isPlaceholder) return <span className={placeholderClass()}>{placeholder}</span>;
        const key = state?.selectedKey;
        const node = key == null ? null : state?.collection.getItem(key);
        const meta = readOptionMeta(node?.props);
        if (meta?.color) {
          return (
            <Tag color={meta.color} size={size === 'sm' ? 'sm' : 'md'}>
              {selectedText}
            </Tag>
          );
        }
        const Glyph = meta?.icon;
        return (
          <>
            {meta?.avatar ?? (Glyph ? <Glyph aria-hidden="true" className={icon()} /> : null)}
            <span className={text()}>{selectedText}</span>
          </>
        );
      }}
    </AriaSelectValue>
  );
}

/* Read from the collection, which only exists inside the Select. An id
 * with no option shows as itself, so a stale value stays visible and
 * removable. */
function SelectChips({
  ids,
  placeholder,
  ...chipsProps
}: Omit<React.ComponentProps<typeof PickerChips>, 'entries'> & {
  ids: string[];
  placeholder?: string;
}) {
  const state = React.useContext(SelectStateContext);
  const { chips, placeholder: placeholderClass } = selectStyles({
    size: chipsProps.size,
    singleLine: !!chipsProps.isSingleLine,
  });
  const entries: ChipEntry[] = ids.map((id) => {
    const node = state?.collection.getItem(id);
    return node
      ? { id, label: node.textValue, meta: readOptionMeta(node.props) }
      : { id, label: id };
  });
  return (
    <div className={chips()}>
      {entries.length === 0 ? (
        <span className={placeholderClass()}>{placeholder}</span>
      ) : (
        <PickerChips {...chipsProps} entries={entries} />
      )}
    </div>
  );
}

/* The frame reads the open state and the collection from React Aria's
 * select state, which only exists inside the Select element. */
function SelectFrame({
  frameRef,
  buttonRef,
  suppressed,
  isInert,
  isMultiple,
  hasChips,
  isListPresent,
  size,
  state,
  children,
}: {
  frameRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  suppressed: React.RefObject<Set<string>>;
  isInert: boolean;
  isMultiple: boolean;
  hasChips: boolean;
  isListPresent: boolean;
  size: 'sm' | 'md' | 'lg';
  state: FrameState;
  children: React.ReactNode;
}) {
  const select = React.useContext(SelectStateContext);

  /* A press on the frame's padding or its chevron is the trigger's own (the
   * focusEntryOnPress precedent for entries), opened the way the trigger
   * opens itself so focus lands on the chosen option; a press on the seated
   * clear, or on a chip, stays theirs. */
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('button, [data-component="chip"]')) return;
    e.preventDefault();
    buttonRef.current?.focus();
    if (!isInert) select?.toggle(null);
  };

  /* The closed trigger's arrows step past options that cannot be chosen.
   * React Aria's own step lands on an unavailable option (enabled for it, so
   * reachable) and the bridge drops the move, which would leave the value
   * stuck beside it. */
  const onKeyDownCapture = (e: React.KeyboardEvent) => {
    if (!select || select.isOpen || isInert || isMultiple || e.target !== buttonRef.current) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const keys = optionKeys(select.collection);
    const from = select.selectedKey == null ? -1 : keys.indexOf(select.selectedKey);
    // With nothing chosen, either arrow starts at the first option, as React Aria's own does.
    const step = from < 0 || e.key === 'ArrowRight' ? 1 : -1;
    let index = from;
    let next: Key | null = null;
    for (index += step; index >= 0 && index < keys.length; index += step) {
      const key = keys[index];
      if (key == null) break;
      const props = select.collection.getItem(key)?.props as { isDisabled?: boolean } | undefined;
      if (props?.isDisabled || suppressed.current?.has(String(key))) continue;
      next = key;
      break;
    }
    e.preventDefault();
    e.stopPropagation();
    if (next != null) select.setSelectedKey(next);
  };

  return (
    <Group
      ref={frameRef}
      className={frameStyles({
        size,
        state,
        kind: 'trigger',
        multiline: false,
        chips: isMultiple,
        hasChips,
        // The multiple form's opener is a seat at the frame's end.
        hasAction: isMultiple,
        /* Until the list has left: focus comes back to the trigger only
         * then, and the field must not look unfocused in between. */
        open: (select?.isOpen ?? false) || isListPresent,
      })}
      onMouseDown={onMouseDown}
      onKeyDownCapture={onKeyDownCapture}
    >
      {children}
    </Group>
  );
}

/* The filter, and its no-match line, only over options to narrow: a Select
 * with none says why there are none, never that a filter found nothing. */
function SelectOptions({
  filter,
  list,
  emptyList,
}: {
  filter: (list: React.ReactNode) => React.ReactNode;
  list: React.ReactNode;
  emptyList: React.ReactNode;
}) {
  const state = React.useContext(SelectStateContext);
  return state && state.collection.size === 0 ? emptyList : filter(list);
}

function SelectPopup({
  frameRef,
  onPresenceChange,
  children,
}: {
  frameRef: React.RefObject<HTMLDivElement>;
  onPresenceChange: (isPresent: boolean) => void;
  children: React.ReactNode;
}) {
  const state = React.useContext(SelectStateContext);
  return (
    <PickerPopup
      name="select"
      isOpen={state?.isOpen ?? false}
      triggerRef={frameRef}
      onPresenceChange={onPresenceChange}
    >
      {children}
    </PickerPopup>
  );
}

/**
 * One choice from a closed list, with its label, help and error: the field
 * anatomy around a button that opens the options (field.md, picker.md).
 * Flat props, the Form deciding where the label sits, the error replacing
 * the description, `aria` validation so the owner decides when the value
 * is wrong. The value is the chosen Option's `id`; with
 * `selectionMode="multiple"`, several choices as removable chips in the
 * frame, the value their ids in the order chosen.
 *
 * The forwarded ref is the trigger button, the focusable, so a form library
 * focusing the first invalid field lands on it.
 *
 * A Select with no options still opens, and its list says why
 * (`emptyMessage`): a trigger that takes the press and shows nothing reads
 * as broken. A filterable one shows its filter only over options, so "none
 * yet" and "none match" (`filterEmptyMessage`) never share a sentence. Its trigger, a button, carries no `aria-required` or
 * `aria-invalid`; required reaches assistive technology through the label's
 * mark, invalid through the error the trigger is described by.
 */
export const Select = forwardRef(function Select<T extends object>(
  {
    label,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    selectionMode = 'single',
    removeLabel,
    isSingleLine,
    description,
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    placeholder,
    size = 'md',
    isClearable,
    clearLabel,
    isFilterable,
    filterLabel,
    filterPlaceholder,
    filterEmptyMessage,
    emptyMessage,
    value,
    defaultValue,
    onChange,
    isOpen,
    defaultOpen,
    onOpenChange,
    onBlur,
    onFocus,
    items,
    children,
    dependencies,
    ...rest
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const buttonRef = useObjectRef(ref);
  const frameRef = React.useRef<HTMLDivElement>(null);
  const { labelPosition, styles } = useFieldLayout(size);
  const { root, label: labelClass, column } = styles;
  const { trigger, filter } = selectStyles({ size, readOnly: isReadOnly });
  const { icon } = adornmentStyles({ size });
  const { icon: filterGlyph } = adornmentStyles({ size: 'md' });
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);
  const { suppressed, context: suppression } = useListSuppression();
  const isInert = isDisabled || isReadOnly;
  const isMultiple = selectionMode === 'multiple';
  const chipsId = React.useId();
  /* React Aria restores its own default on a native form reset; the picker
   * hands it the value it started with, so a reset lands on that. */
  const [initialValue] = React.useState<PickerValue>(
    () => defaultValue ?? value ?? (selectionMode === 'multiple' ? [] : null),
  );

  const [current, setCurrent] = useOwnedState<PickerValue>(
    value,
    defaultValue ?? (isMultiple ? [] : null),
    onChange as ((next: PickerValue) => void) | undefined,
    samePickerValue,
  );
  const chosen = Array.isArray(current) ? current : [];
  const [open, setOpen] = useOwnedState<boolean>(isOpen, defaultOpen ?? false, onOpenChange);
  const [isListPresent, setListPresent] = React.useState(false);

  /* React Aria closes the list on every selection, a suppressed one
   * included, in the same tick: the flag lets that one close pass by so the
   * option's reason stays readable; the close consumes it, and it clears
   * itself in case none follows. Read-only holds the value too: typeahead
   * and the closed trigger's arrows arrive here as well. */
  const suppressedPress = React.useRef(false);
  const handleChange = (key: Key | Key[] | null) => {
    if (isReadOnly) return;
    if (Array.isArray(key)) {
      setCurrent(withoutSuppressedToggles(key.map(String), chosen, suppressed.current));
      return;
    }
    const next = key == null ? null : String(key);
    if (next != null && suppressed.current.has(next)) {
      suppressedPress.current = true;
      queueMicrotask(() => {
        suppressedPress.current = false;
      });
      return;
    }
    setCurrent(next);
  };
  /* Re-pressing the chosen option, now unavailable, reports no change, only
   * the close: the press is noted on the document's capture, before React
   * Aria handles it, and forgotten once its events are done. */
  const pressedUnavailable = React.useRef(false);
  React.useEffect(() => {
    if (!open) return;
    const note = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ') return;
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest('[role="option"][aria-disabled="true"]')) return;
      pressedUnavailable.current = true;
      setTimeout(() => {
        pressedUnavailable.current = false;
      }, 0);
    };
    const types = ['pointerdown', 'pointerup', 'click', 'keydown', 'keyup'];
    for (const type of types) document.addEventListener(type, note, true);
    return () => {
      for (const type of types) document.removeEventListener(type, note, true);
    };
  }, [open]);
  const handleOpenChange = (next: boolean) => {
    if (!next && (suppressedPress.current || pressedUnavailable.current)) {
      suppressedPress.current = false;
      return;
    }
    if (next && isInert) return;
    setOpen(next);
  };

  /* The single form's clear, or the single-line form's clear for every chip
   * (the chips behind "+N" cannot be removed one by one). */
  const showsClear =
    clearLabel != null &&
    !isInert &&
    (isMultiple ? !!isSingleLine && chosen.length > 0 : !!isClearable && current != null);
  const removeChips = isInert
    ? undefined
    : (ids: string[]) => {
        const next = chosen.filter((id) => !ids.includes(id));
        setCurrent(next);
        if (next.length === 0) buttonRef.current?.focus();
      };
  const seat = React.useMemo(
    () => ({
      size: size === 'sm' ? ('md' as const) : size,
      isDisabled,
      isTransient: true,
      besideChips: isMultiple,
    }),
    [size, isDisabled, isMultiple],
  );
  const list = (
    <OptionList items={items} dependencies={dependencies} emptyMessage={emptyMessage}>
      {children}
    </OptionList>
  );
  const filteredList = (
    <OptionList items={items} dependencies={dependencies} emptyMessage={filterEmptyMessage}>
      {children}
    </OptionList>
  );

  const field = (
    <AriaSelect
      {...rest}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
      selectionMode={selectionMode}
      value={current}
      defaultValue={initialValue}
      onChange={handleChange}
      isOpen={open && !isInert}
      onOpenChange={handleOpenChange}
      allowsEmptyCollection
      isRequired={isRequired}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      validationBehavior="aria"
      /* The opener names the field; the chips it is described by say what
       * is chosen. */
      aria-describedby={
        [isMultiple && chosen.length > 0 ? chipsId : null, describedBy].filter(Boolean).join(' ') ||
        undefined
      }
      onBlur={onBlur as ((e: React.FocusEvent<Element>) => void) | undefined}
      onFocus={onFocus as ((e: React.FocusEvent<Element>) => void) | undefined}
      className="ck:contents"
    >
      {label ? (
        <FieldLabel isRequired={isRequired} announcesRequired className={labelClass()}>
          {label}
        </FieldLabel>
      ) : null}
      <div className={column()}>
        <SelectFrame
          frameRef={frameRef}
          buttonRef={buttonRef}
          suppressed={suppressed}
          isInert={isInert}
          isMultiple={isMultiple}
          hasChips={chosen.length > 0}
          isListPresent={isListPresent}
          size={size}
          state={frameState({ isDisabled, isReadOnly, isInvalid })}
        >
          {isMultiple ? (
            <SelectChips
              id={chipsId}
              ids={chosen}
              label={label ?? ariaLabel ?? ''}
              labelledBy={ariaLabelledBy}
              placeholder={placeholder}
              size={size === 'lg' ? 'lg' : 'md'}
              removeLabel={removeLabel}
              onRemove={removeChips}
              isDisabled={isDisabled}
              isSingleLine={isSingleLine}
            />
          ) : (
            <AriaButton
              ref={buttonRef}
              className={entryStyles({
                size,
                multiline: false,
                readOnly: isReadOnly,
                class: trigger(),
              })}
            >
              <SelectValueText placeholder={placeholder} size={size} />
            </AriaButton>
          )}
          {showsClear ? (
            /* React Aria hands the trigger's props to any Button inside the
             * Select; the clear is its own button and takes none of them. */
            <AriaButtonContext.Provider value={null}>
              <FieldActionContext.Provider value={seat}>
                <FieldAction
                  icon={XMarkIcon}
                  label={clearLabel}
                  onPress={() => {
                    setCurrent(isMultiple ? [] : null);
                    buttonRef.current?.focus();
                  }}
                />
              </FieldActionContext.Provider>
            </AriaButtonContext.Provider>
          ) : null}
          {isMultiple ? (
            /* The trigger, bare: a chip's remove button cannot live inside a
             * button, so the opener stands after the chips, in the seat. */
            <AriaButton
              ref={buttonRef}
              className={fieldSeatClass(size === 'lg' ? 'lg' : 'md', {
                isInert,
                besideChips: true,
                afterSeat: showsClear,
              })}
              data-component="select-opener"
              data-tone="neutral"
            >
              <ChevronDownIcon aria-hidden="true" className={icon()} />
            </AriaButton>
          ) : (
            <ChevronDownIcon aria-hidden="true" className={icon()} />
          )}
        </SelectFrame>
        <FieldMessage
          description={description}
          error={error}
          descriptionId={descriptionId}
          errorId={errorId}
        />
      </div>
      <ListSuppressionContext.Provider value={suppression}>
        <SelectPopup frameRef={frameRef} onPresenceChange={setListPresent}>
          {isFilterable ? (
            <SelectOptions
              list={filteredList}
              emptyList={list}
              filter={(options) => (
                <Autocomplete>
                  <div className={filter()}>
                    <AriaSearchField
                      aria-label={filterLabel}
                      autoFocus
                      className={frameStyles({
                        size: 'md',
                        multiline: false,
                        hasAction: false,
                        state: 'default',
                      })}
                    >
                      <MagnifyingGlassIcon aria-hidden="true" className={filterGlyph()} />
                      <AriaInput
                        className={entryStyles({ size: 'md', multiline: false, search: true })}
                        placeholder={filterPlaceholder}
                      />
                    </AriaSearchField>
                  </div>
                  {options}
                </Autocomplete>
              )}
            />
          ) : (
            list
          )}
        </SelectPopup>
      </ListSuppressionContext.Provider>
    </AriaSelect>
  );
  /* React Aria renders its collection's template beside the Select's own
   * element; this box holds both, so a host spacing its children (a Chakra
   * Stack) sees one field. */
  return (
    <div
      className={root()}
      data-component="select"
      data-label-position={labelPosition}
      data-readonly={isReadOnly || undefined}
      data-disabled={isDisabled || undefined}
      data-invalid={isInvalid || undefined}
      data-required={isRequired || undefined}
    >
      {field}
    </div>
  );
});
